import { Flex, List, Text, ListItem, Box, Grid } from '@chakra-ui/react';
import { useRouter } from 'next/router';
import { withSiteExtensionData } from '../lib/hoc';
import { useTranslation } from '../lib/hooks.context';
import { formatDate, parseDate } from '../lib/utils.date';
import { MhyChakraProps, NewsTeaser } from '../types';
import BasicTeaser, { TeaserHeader } from './BasicTeaser';
import ButtonLink from './ButtonLink';
import Heading from './Heading';
import SiteSearch from './SiteSearch';

interface Props {
  jobs: NewsTeaser[];
  isLoading?: boolean;
}

// Alternative style if this isn't good
// const JobListItem = ({ id, title, url, image, lead, date, site, ...rest }: NewsTeaser & MhyChakraProps) => (
//   <Flex flexWrap="wrap" justifyContent="space-between" alignItems="center" {...rest}>
//     <Flex flexDir="column">
//       <ArticleInfo {...{date, site}} mb="2" />
//       <Heading mt={0} as="h2" variant="h4">
//         {title}
//       </Heading>
//     </Flex>
//     <ButtonLink link={{url, title: 'Lue lisää'}} variant="solidGreen" />
//   </Flex>
// )

const JobTeaser = ({
  id,
  image,
  lead,
  title,
  url,
  date,
  site,
  ...rest
}: NewsTeaser & MhyChakraProps) => {
  const t = useTranslation();
  const start = date ? parseDate(date, 'dd.MM.yyyy') : null;
  const { locale } = useRouter();

  return (
    <BasicTeaser
      width="100%"
      maxWidth="500px"
      {...rest}
      hasTeaserHeader={!!site}
    >
      {site && (
        <TeaserHeader>
          {start && (
            <Text as="time" textStyle="eventDate">
              {formatDate(start, locale, true)}
            </Text>
          )}
          <Text as="span" textStyle="sm" textAlign="right">
            {site === 'Valtakunnallinen' ? t('main_site') : site}
          </Text>
        </TeaserHeader>
      )}
      <Heading variant="h5" as="h2">
        {title}
      </Heading>
      <ButtonLink
        link={{ url, title: t('read_more') }}
        variant="outlineGreen"
      />
    </BasicTeaser>
  );
};

const JobList = ({ jobs, isLoading }: Props) => {
  const t = useTranslation();

  return (
    <Box as="section">
      <Flex
        as="header"
        mb={[8, 10, 14]}
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gridGap="4"
        sx={{ '> *': { width: 'auto' } }}
      >
        <SiteSearch isLoading={isLoading} />
      </Flex>
      {!jobs?.length ? (
        <Text>{t('jobs_no_items')}</Text>
      ) : (
        <Grid
          templateColumns={[
            'repeat(auto-fit, 100%)',
            'repeat(auto-fit, calc(50% - var(--mhy-space-5)))',
            'repeat(auto-fit, calc(33.3333% - var(--mhy-space-7)))',
            'repeat(auto-fit, calc(25% - var(--mhy-space-8)))',
          ]}
          gridColumnGap={['6', '10']}
          gridRowGap={['6', '10', null, '16']}
          mb="10"
          as={List}
        >
          {jobs.map((post) => (
            <JobTeaser
              key={`${post.id || post.title}-${post.site}`}
              as={ListItem}
              borderBottom="1px solid"
              borderBottomColor="borderColor"
              {...post}
            />
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default withSiteExtensionData<Pick<Props, 'jobs'>>(JobList, {
  param: 'sites',
  dataKey: 'jobs',
});
